import type {
  SmsBlockReason,
  SmsSingleStatus,
  SmsSingleStatusResult,
} from "../../services/sms.service";

/**
 * The contact's single SMS status (migration 2026-10-26-998) for the primary phone, where texts go.
 * "Opted out" is shown ONLY on explicit evidence that the member opted out; a contact with no recorded opt-in reads
 * "No text consent on file", which is never a choice by the member. Display only: sending is decided elsewhere.
 */
export const SMS_SINGLE_STATUS_LABELS: Record<SmsSingleStatus, string> = {
  can_text: "Can text (opted in)",
  opted_out: "Opted out (an explicit opt-out or a STOP reply from this number)",
  number_blocked: "Texts to this number are blocked",
  no_mobile: "No usable SMS phone number",
  no_consent_on_file: "No text consent on file",
};

export const SMS_BLOCK_REASON_LABELS: Record<SmsBlockReason, string> = {
  provider_stop: "a STOP reply from this number",
  undeliverable: "the number is undeliverable",
  complaint: "a complaint about texts to this number",
  manual: "blocked manually",
  opt_out_flag:
    "an opt-out flag on a contact record using this number, possibly another contact",
};

const HINTS: Partial<Record<SmsSingleStatus, string>> = {
  no_consent_on_file:
    "The CRM has no recorded opt-in for this contact. This is not an opt-out.",
};

export function smsSingleStatusText(data: SmsSingleStatusResult): string {
  const base = SMS_SINGLE_STATUS_LABELS[data.sms_status] ?? data.sms_status;
  return data.sms_block_reason
    ? `${base} — ${SMS_BLOCK_REASON_LABELS[data.sms_block_reason] ?? data.sms_block_reason}`
    : base;
}

export default function ContactSmsStatus({
  data,
  isLoading,
  isError,
}: {
  data?: SmsSingleStatusResult;
  isLoading: boolean;
  isError: boolean;
}) {
  const s = data?.sms_status;
  const hint = s ? HINTS[s] : undefined;
  return (
    <div data-testid="contact-sms-status">
      <p
        className={`mt-1 text-sm ${s === "can_text" ? "text-green-700" : s === "opted_out" || s === "number_blocked" ? "font-medium text-red-700" : "text-gray-600"}`}
      >
        {isLoading
          ? "Checking SMS consent…"
          : isError || !data
            ? "SMS consent could not be checked"
            : smsSingleStatusText(data)}
      </p>
      {!isLoading && !isError && hint && (
        <p className="text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );
}
